import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Input,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Text,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Heading,
  useToast,
} from "@chakra-ui/react";
import { ChangePasswordAsync } from "../Slice/PasswordChange/PasswordChangeSlice";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import bg from "../image/image1.png";

// Import Heroicons v2
const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-5 h-5"
  >
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    <path
      fillRule="evenodd"
      d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-5 h-5"
  >
    <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
    <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
    <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
  </svg>
);

const UpdatePasswordForm = () => {
  const dispatch = useDispatch();
  const toast = useToast(); // Add toast hook

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // State for toggling password visibility
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match!");
      // Show error toast for password mismatch
      toast({
        title: "Error",
        description: "Passwords do not match!",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await dispatch(
        ChangePasswordAsync({
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: confirmPassword,
        })
      ).unwrap();

      setMessage("Password updated successfully!");

      // Show success toast
      toast({
        title: "Success",
        description: "Password updated successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });

      // Redirect to home page after success
      setTimeout(() => {
        navigate("/");
      }, 1500); // Adding delay for better UX
    } catch (error) {
      const errorMessage = error.message || "Failed to update password";
      setMessage(errorMessage);

      // Show error toast
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Chakra UI color mode values for styling
  const bgGradient = "linear(to-r, blue.400, purple.500)";
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const inputBg = useColorModeValue("white", "gray.700");
  const successColor = useColorModeValue("green.500", "green.300");
  const errorColor = useColorModeValue("red.500", "red.300");
  const overlayBg = useColorModeValue(
    "rgba(240, 240, 245, 0.8)",
    "rgba(20, 20, 25, 0.8)"
  );

  return (
    // Full page overlay with low opacity background
    <div className="">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4   sm:py-4 min-h-screen">
          <div>
            <h1 className="sm:text-4xl sm:px-24 text-2xl font-semibold mb-4 text-left">
              Change Password
            </h1>
          </div>
          {/* Card Container with subtle animation */}
          <div className="  rounded-xl sm:px-24 ">
            <Box>
              <Box p="6" bg={cardBg}>
                <form onSubmit={handleSubmit}>
                  <VStack spacing={6}>
                    {/* Current Password */}
                    <FormControl isRequired>
                      <FormLabel fontWeight="medium">
                        Current Password
                      </FormLabel>
                      <InputGroup size="md">
                        <Input
                          type={showPassword.current ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          bg={inputBg}
                          borderRadius="md"
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow: "0 0 0 1px blue.400",
                          }}
                          placeholder="Current Password"
                        />
                        <InputRightElement>
                          <Box
                            as="button"
                            type="button"
                            onClick={() => togglePasswordVisibility("current")}
                            color="gray.500"
                            _hover={{ color: "gray.700" }}
                            h="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {showPassword.current ? (
                              <EyeSlashIcon />
                            ) : (
                              <EyeIcon />
                            )}
                          </Box>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {/* New Password */}
                    <FormControl isRequired>
                      <FormLabel fontWeight="medium">New Password</FormLabel>
                      <InputGroup size="md">
                        <Input
                          type={showPassword.new ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          bg={inputBg}
                          borderRadius="md"
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow: "0 0 0 1px blue.400",
                          }}
                          placeholder="New Password"
                        />
                        <InputRightElement>
                          <Box
                            as="button"
                            type="button"
                            onClick={() => togglePasswordVisibility("new")}
                            color="gray.500"
                            _hover={{ color: "gray.700" }}
                            h="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {showPassword.new ? <EyeSlashIcon /> : <EyeIcon />}
                          </Box>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {/* Confirm Password */}
                    <FormControl isRequired>
                      <FormLabel fontWeight="medium">
                        Confirm New Password
                      </FormLabel>
                      <InputGroup size="md">
                        <Input
                          type={showPassword.confirm ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          bg={inputBg}
                          borderRadius="md"
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow: "0 0 0 1px blue.400",
                          }}
                          placeholder="Confirm New Password"
                        />
                        <InputRightElement>
                          <Box
                            as="button"
                            type="button"
                            onClick={() => togglePasswordVisibility("confirm")}
                            color="gray.500"
                            _hover={{ color: "gray.700" }}
                            h="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {showPassword.confirm ? (
                              <EyeSlashIcon />
                            ) : (
                              <EyeIcon />
                            )}
                          </Box>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {message && (
                      <Box
                        p={3}
                        borderRadius="md"
                        bg={message.includes("success") ? "green.50" : "red.50"}
                        color={
                          message.includes("success")
                            ? successColor
                            : errorColor
                        }
                        width="full"
                      >
                        <Text fontSize="sm">{message}</Text>
                      </Box>
                    )}

                    <Button
                      type="submit"
                      isLoading={loading}
                      loadingText="Updating"
                      width="full"
                      bgGradient={bgGradient}
                      color="white"
                      _hover={{
                        bgGradient: "linear(to-r, blue.500, purple.600)",
                        transform: "translateY(-1px)",
                      }}
                      _active={{
                        bgGradient: "linear(to-r, blue.600, purple.700)",
                        transform: "translateY(0)",
                      }}
                      transition="all 0.2s"
                      boxShadow="md"
                      borderRadius="md"
                      height="42px"
                      fontSize="md"
                    >
                      Update Password
                    </Button>
                  </VStack>
                </form>
              </Box>
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePasswordForm;
